import * as Sentry from '@sentry/react';
import { backendUrl } from '_config';
import Cookie from 'js-cookie';

let inflightCreateCsrf: Promise<Response> | null = null;
let csrfReady: Promise<void> | null = null;

const getNetworkMeta = (): {
    online?: boolean;
    userAgent?: string;
    connectionType?: string;
    connectionDownlink?: number;
    connectionRtt?: number;
} => {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const connection =
        nav && 'connection' in nav
            ? (
                  nav as Navigator & {
                      connection?: {
                          type?: string;
                          downlink?: number;
                          rtt?: number;
                      };
                  }
              ).connection
            : undefined;

    return {
        online: nav?.onLine,
        userAgent: nav?.userAgent,
        connectionType: connection?.type,
        connectionDownlink: connection?.downlink,
        connectionRtt: connection?.rtt,
    };
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const isAndroidWebView = (): boolean => {
    const ua =
        typeof navigator !== 'undefined' && navigator.userAgent
            ? navigator.userAgent
            : '';
    return /Android/i.test(ua) && /wv|Version\/\d+\.\d+/i.test(ua);
};

const waitForToken = async (): Promise<string | undefined> => {
    const delays = [0, 30, 120]; // small cumulative wait (<=150ms)
    for (const d of delays) {
        if (d) await sleep(d);
        const token = Cookie.get('XSRF-TOKEN');
        if (token) return token;
    }
    return undefined;
};

const Csrf = {
    async ensureReady(): Promise<void> {
        if (!csrfReady) {
            csrfReady = (async () => {
                Sentry.addBreadcrumb({
                    category: 'csrf',
                    level: 'info',
                    message: 'ensureReady start',
                    data: {
                        cookieEnabled:
                            typeof navigator !== 'undefined'
                                ? navigator.cookieEnabled
                                : undefined,
                        network: getNetworkMeta(),
                    },
                });
                const existing = Cookie.get('XSRF-TOKEN');
                if (existing) return;
                await Csrf.createCsrfCookie();
                const tokenAfter = await waitForToken();
                if (!tokenAfter)
                    throw new Error('CSRF token missing after create');
            })();
        }
        try {
            await csrfReady;
        } catch (error) {
            Sentry.captureException(error, {
                level: 'error',
                tags: { requestKind: 'CsrfEnsureReady' },
                extra: {
                    backendUrl,
                    windowOrigin:
                        typeof window !== 'undefined'
                            ? window.location.origin
                            : undefined,
                    currentUrl:
                        typeof window !== 'undefined'
                            ? window.location.href
                            : undefined,
                    cookieEnabled:
                        typeof navigator !== 'undefined'
                            ? navigator.cookieEnabled
                            : undefined,
                    network: getNetworkMeta(),
                },
            });
            csrfReady = null; // allow next call to retry
            throw error;
        }
    },
    async getCookie(): Promise<Response | string> {
        const token = Cookie.get('XSRF-TOKEN');

        if (token) {
            return Promise.resolve(token);
        }
        // Fallback to creating the cookie when absent
        const response = await Csrf.createCsrfCookie();
        return response;
    },
    async createCsrfCookie(): Promise<Response> {
        if (inflightCreateCsrf) return inflightCreateCsrf;

        const url = backendUrl + 'sanctum/csrf-cookie';
        const abortAfterMs = 5000;

        const attempt = async (n = 0): Promise<Response> => {
            const controller = new AbortController();
            const keepalive = !isAndroidWebView();
            const abortTimer = setTimeout(
                () => controller.abort('csrf-timeout'),
                abortAfterMs,
            );
            try {
                Sentry.addBreadcrumb({
                    category: 'csrf',
                    level: 'info',
                    message: 'csrf-cookie attempt',
                    data: {
                        url,
                        attempt: n + 1,
                        keepalive,
                        abortAfterMs,
                        network: getNetworkMeta(),
                    },
                });
                const resp = await fetch(url, {
                    credentials: 'include',
                    mode: 'cors',
                    keepalive,
                    signal: controller.signal,
                });
                if (!resp.ok) throw new Error(`csrf-cookie ${resp.status}`);
                Sentry.addBreadcrumb({
                    category: 'csrf',
                    level: 'info',
                    message: 'csrf-cookie ok',
                    data: {
                        url,
                        setCookieHeaderPresent: resp.headers.has('set-cookie'),
                        cookieEnabled:
                            typeof navigator !== 'undefined'
                                ? navigator.cookieEnabled
                                : undefined,
                        network: getNetworkMeta(),
                    },
                });
                return resp;
            } catch (err) {
                const abortedByTimeout =
                    controller.signal.aborted &&
                    controller.signal.reason === 'csrf-timeout';
                if (abortedByTimeout) {
                    Sentry.addBreadcrumb({
                        category: 'csrf',
                        level: 'warning',
                        message: 'csrf-cookie aborted',
                        data: {
                            url,
                            attempt: n + 1,
                            keepalive,
                            abortAfterMs,
                            network: getNetworkMeta(),
                        },
                    });
                }
                if (n === 0) {
                    // single retry, not a loop
                    await sleep(200);
                    return attempt(1);
                }
                throw err;
            } finally {
                clearTimeout(abortTimer);
            }
        };

        inflightCreateCsrf = attempt()
            .catch((error) => {
                Sentry.captureException(error, {
                    level: 'error',
                    tags: { requestKind: 'CsrfCookie' },
                    extra: {
                        url,
                        method: 'GET',
                        backendUrl,
                        windowOrigin:
                            typeof window !== 'undefined'
                                ? window.location.origin
                                : undefined,
                        currentUrl:
                            typeof window !== 'undefined'
                                ? window.location.href
                                : undefined,
                        network: getNetworkMeta(),
                    },
                });
                throw error;
            })
            .finally(() => {
                inflightCreateCsrf = null;
            });

        return inflightCreateCsrf;
    },
};

export default Csrf;
